import React, { useState } from 'react';
import { HbrTab, HbrTabGroup, HbrTabPanel } from '@harbor/elements-react-standalone';
import LabAgentsPanel from './LabAgentsPanel';
import LabAssignmentsPanel from './LabAssignmentsPanel';
import LabAssistPanel from './LabAssistPanel';
import LabInjectionsPanel from './LabInjectionsPanel';
import LabPoolsPanel from './LabPoolsPanel';
import LabSweepsPanel from './LabSweepsPanel';

type Tab = 'pools' | 'assignments' | 'assist' | 'agents' | 'sweeps' | 'injections';

export type LabOperationsPageProps = {
  /**
   * Whether to offer steward actions: stocking pools, assigning pods, enrolling and revoking
   * agents, re-sweeping a push. Every one of them is checked again on the backend against the
   * caller's token, so this only decides what is worth showing.
   */
  canSteward?: boolean;
};

const TABS: { key: Tab; label: string }[] = [
  { key: 'pools', label: 'Pools' },
  { key: 'assignments', label: 'Assignments' },
  { key: 'assist', label: 'Assist' },
  { key: 'agents', label: 'Agents' },
  { key: 'sweeps', label: 'Sweeps' },
  { key: 'injections', label: 'Mid-session pushes' },
];

/**
 * Running an event: who has a pod, who is stuck, which agents are alive, what went out.
 *
 * Kept apart from scenario authoring on purpose, and that split is the backend's too - a
 * half-written step cannot go out because somebody saved it. Nothing on this page edits a
 * scenario; everything here acts on pods that are already running.
 */
const LabOperationsPage: React.FC<LabOperationsPageProps> = ({ canSteward = false }) => {
  const [tab, setTab] = useState<Tab>('pools');

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <h1 className="m-0 text-xl font-semibold">Lab operations</h1>
        <p className="m-0 text-sm text-gray-600">
          Pods, people and agents, while the event is running.
        </p>
      </div>

      <HbrTabGroup primary>
        {TABS.map((entry) => (
          <HbrTab
            slot="nav"
            panel={entry.key}
            key={entry.key}
            active={tab === entry.key}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </HbrTab>
        ))}

        <HbrTabPanel name="pools">
          {tab === 'pools' && <LabPoolsPanel canSteward={canSteward} />}
        </HbrTabPanel>

        <HbrTabPanel name="assignments">
          {tab === 'assignments' && <LabAssignmentsPanel canSteward={canSteward} />}
        </HbrTabPanel>

        <HbrTabPanel name="assist">{tab === 'assist' && <LabAssistPanel />}</HbrTabPanel>

        <HbrTabPanel name="agents">
          {tab === 'agents' && <LabAgentsPanel canManage={canSteward} />}
        </HbrTabPanel>

        <HbrTabPanel name="sweeps">{tab === 'sweeps' && <LabSweepsPanel />}</HbrTabPanel>

        <HbrTabPanel name="injections">
          {tab === 'injections' && <LabInjectionsPanel canManage={canSteward} />}
        </HbrTabPanel>
      </HbrTabGroup>
    </div>
  );
};

export default LabOperationsPage;
